import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/** Skim filter: pointer must dwell this long before MegaMenuHoverEvent fires. */
const HOVER_COMMIT_DELAY_MS = 150;

/** Extra time to cross the header gap into the overflow panel (MORE has no in-item submenu). */
const OVERFLOW_CLOSE_DELAY_MS = 250;

/**
 * Opt-in menu tracing for keyboard/hover testing.
 * Enable in the browser console: localStorage.setItem('kilwins-header-menu-debug', '1')
 * Disable: localStorage.removeItem('kilwins-header-menu-debug')
 */
const HEADER_MENU_DEBUG = (() => {
  try {
    return localStorage.getItem('kilwins-header-menu-debug') === '1';
  } catch {
    return false;
  }
})();

/**
 * @param {string} event
 * @param {Record<string, unknown>} [detail]
 */
function logHeaderMenu(event, detail = {}) {
  if (!HEADER_MENU_DEBUG) return;
  console.info(`[header-menu] ${event}`, detail);
}

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 * @property {HTMLElement | null} activeOverflowItem - The overflow item shown when the More trigger is active.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /**
   * @type {MutationObserver | null}
   */
  #submenuMutationObserver = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverDispatchTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeDelayTimer;

  connectedCallback() {
    super.connectedCallback();

    logHeaderMenu('init', {
      expectedKeyboardFlow: [
        'Tab → parent link (preview only, Enter follows URL)',
        'Tab → disclosure chevron (still closed)',
        'Enter/Space on chevron → open submenu and focus first link',
        'MORE: same pattern; overflow items (About, In-Store Treats) use link → chevron → Enter/Space',
        'Escape → close and restore focus',
      ],
    });

    this.#sanitizeLeafLinkAria();
    this.#syncOverflowDisclosures();
    this.#syncOverflowTabOrder();
    this.refs.overflowMenu?.addEventListener('reflow', this.#syncOverflowPanel);
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.addEventListener('keydown', this.#onKeydown);
    this.refs.overflowMenu?.addEventListener('pointerenter', this.#overflowPathEnterListener);
    this.refs.overflowMenu?.addEventListener('pointerleave', this.#overflowPathLeaveListener);
    this.overflowMenu?.addEventListener('pointerenter', this.#overflowPathEnterListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowPathLeaveListener);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#sanitizeLeafLinkAria();
    this.#syncOverflowDisclosures();
    this.#syncOverflowTabOrder();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    this.removeEventListener('keydown', this.#onKeydown);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.refs.overflowMenu?.removeEventListener('pointerenter', this.#overflowPathEnterListener);
    this.refs.overflowMenu?.removeEventListener('pointerleave', this.#overflowPathLeaveListener);
    this.overflowMenu?.removeEventListener('pointerenter', this.#overflowPathEnterListener);
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowPathLeaveListener);
    this.refs.overflowMenu?.removeEventListener('reflow', this.#syncOverflowPanel);
    this.#cleanupMutationObserver();
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;
    clearTimeout(this.#closeDelayTimer);
    this.#closeDelayTimer = undefined;
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowPathEnterListener = () => {
    clearTimeout(this.#closeDelayTimer);
    this.#closeDelayTimer = undefined;
  };

  #overflowPathLeaveListener = (event) => {
    if (!(event instanceof PointerEvent)) return;

    const relatedTarget = event.relatedTarget;
    if (relatedTarget instanceof Node && this.#isMovingToOverflowMenu(relatedTarget)) return;

    clearTimeout(this.#closeDelayTimer);
    this.#closeDelayTimer = setTimeout(() => {
      this.#closeDelayTimer = undefined;
      if (this.#shouldKeepOverflowOpen()) return;
      this.#deactivate();
    }, OVERFLOW_CLOSE_DELAY_MS);
  };

  /**
   * @param {PointerEvent | FocusEvent} event
   * @param {Element | null | undefined} listItem
   * @returns {boolean}
   */
  #isKeyboardTabFocus(event, listItem) {
    if (!(event instanceof FocusEvent)) return false;

    const focused =
      listItem instanceof Element ? (listItem.querySelector(':focus') ?? event.target) : event.target;

    return focused instanceof Element && focused.matches(':focus-visible');
  }

  /**
   * @param {HTMLElement | null | undefined} submenu
   */
  #focusFirstSubmenuLink(submenu) {
    if (!(submenu instanceof HTMLElement)) return;

    const focusable = submenu.querySelector(
      'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
    );

    if (focusable instanceof HTMLElement) {
      focusable.focus({ preventScroll: true });
    }
  }

  /**
   * Escape closes submenus. Parent links follow their URL on Enter; the disclosure
   * button (second tab stop) opens submenu content on Enter/Space.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      const item = this.#state.activeItem;
      if (!item) return;

      logHeaderMenu('keydown.escape', { activeItem: this.#menuItemLabel(item) });

      const control = this.#focusTargetAfterClose(item);
      this.#deactivate(item, { force: true });
      control.focus();
      return;
    }

    if (event.key !== 'Enter' && event.key !== ' ') return;

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    if (target.closest('[ref="disclosure[]"]')) {
      logHeaderMenu('keydown.activate-disclosure', {
        key: event.key,
        label: this.#elementLabel(target),
      });
      return;
    }

    const listItem = target.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';

    // MORE label is the first tab stop — keyboard users open the panel from its disclosure.
    if (slot === 'more' && target.matches('[ref="menuitem"]')) {
      logHeaderMenu('keydown.block-more-label', {
        key: event.key,
        expected: 'Tab to MORE chevron, then Enter/Space to open overflow panel',
      });
      event.preventDefault();
    }
  };

  /**
   * Toggle a submenu from its dedicated disclosure button (click / Enter / Space).
   * @param {PointerEvent | MouseEvent} event
   */
  toggle = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;

    const item = findMenuItem(button);
    if (!item) return;

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const submenu = findSubmenu(item);
    const isExpanded =
      slot === 'more'
        ? this.#isOverflowMenuPathActive()
        : submenu instanceof HTMLElement && !submenu.inert;

    logHeaderMenu('toggle', {
      label: this.#menuItemLabel(item),
      slot,
      isExpanded,
      submenuInert: submenu instanceof HTMLElement ? submenu.inert : null,
      trigger: this.#elementLabel(button),
    });

    if (isExpanded) {
      // Close whatever is currently active. In the overflow panel the initially
      // selected item is tracked as `activeOverflowItem` while the More trigger is
      // the `activeItem`, so deactivating `item` directly would no-op; deactivate
      // the active item instead.
      this.#deactivate(this.#state.activeItem ?? item, { force: true });
      // Closing the overflow panel hides the focused disclosure, so return focus
      // to the visible More trigger; a top-level disclosure stays put.
      const focusTarget = this.#focusTargetAfterClose(item);
      if (focusTarget !== button) focusTarget.focus();
    } else {
      if (slot === 'overflow') {
        if (!this.#isOverflowMenuPathActive()) {
          const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
          if (moreButton instanceof HTMLElement) {
            this.activate({ target: moreButton }, { forceOpen: true });
          }
        }

        this.#state.activeOverflowItem = item;
        this.#setOverflowSubmenuActive(item, { interactive: true });
      }

      this.activate(event, { forceOpen: true });

      if (slot === 'more') {
        this.#setExpandedState(item, true);
        return;
      }

      if (submenu instanceof HTMLElement) {
        submenu.inert = false;
        this.#setExpandedState(item, true);
        this.#focusFirstSubmenuLink(submenu);
      }
    }
  };

  /**
   * The control that should receive focus after a submenu closes. Overflow items
   * live in a panel that is hidden on close, so focus returns to the always-visible
   * More trigger instead of a now-inert control inside the panel.
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #focusTargetAfterClose(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    if (slot === 'overflow' || slot === 'more') {
      const moreButton = this.querySelector('[slot="more"] [ref="menuitem"]');
      if (moreButton instanceof HTMLElement) return moreButton;
    }
    return this.#expandableFor(item) ?? item;
  }

  /**
   * Return the element that carries the `aria-expanded` state for a menu item.
   * Plain links without submenus return null — only disclosure buttons and the
   * More trigger carry expanded state.
   * @param {HTMLElement} item
   * @returns {HTMLElement | null}
   */
  #expandableFor(item) {
    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';

    const disclosure = listItem?.querySelector('[ref="disclosure[]"]');
    if (disclosure instanceof HTMLElement) return disclosure;

    return null;
  }

  /**
   * Set or clear `aria-expanded` on the correct control for a menu item.
   * Leaf links never receive `aria-expanded`.
   * @param {HTMLElement} item
   * @param {boolean} expanded
   */
  #setExpandedState(item, expanded) {
    const expandable = this.#expandableFor(item);
    if (expandable) {
      expandable.ariaExpanded = expanded ? 'true' : 'false';
      logHeaderMenu('aria-expanded', {
        label: this.#menuItemLabel(item),
        expanded,
        control: this.#elementLabel(expandable),
      });
      return;
    }

    const menuitem = item.closest('.menu-list__list-item')?.querySelector('[ref="menuitem"]');
    menuitem?.removeAttribute('aria-expanded');
  }

  /**
   * @param {Element | null | undefined} element
   * @returns {string}
   */
  #elementLabel(element) {
    if (!(element instanceof HTMLElement)) return 'unknown';

    return (
      element.getAttribute('aria-label') ||
      element.textContent?.trim().replace(/\s+/g, ' ').slice(0, 60) ||
      element.tagName.toLowerCase()
    );
  }

  /**
   * @param {HTMLElement | null | undefined} menuItem
   * @returns {string}
   */
  #menuItemLabel(menuItem) {
    if (!(menuItem instanceof HTMLElement)) return 'unknown';
    return this.#elementLabel(menuItem);
  }

  /**
   * @param {HTMLElement | null | undefined} menuItem
   * @returns {Record<string, unknown>}
   */
  #submenuSnapshot(menuItem) {
    const submenu = menuItem ? findSubmenu(menuItem) : null;
    if (!(submenu instanceof HTMLElement)) {
      return { hasSubmenu: false };
    }

    return {
      hasSubmenu: true,
      inert: submenu.inert,
      active: submenu.hasAttribute('data-active'),
      relocated: submenu.hasAttribute('data-overflow-owner'),
      focusableCount: submenu.querySelectorAll(
        'a[href]:not([tabindex="-1"]), button:not([disabled]):not([tabindex="-1"])'
      ).length,
    };
  }

  /**
   * Plain nav links (e.g. Locations) must not carry `aria-expanded` — only disclosure
   * buttons and the More trigger represent expandable menus.
   */
  #sanitizeLeafLinkAria() {
    for (const listItem of this.querySelectorAll('.menu-list__list-item')) {
      if (listItem.querySelector('[ref="disclosure[]"]')) continue;

      const slot = listItem instanceof HTMLElement ? listItem.slot : '';
      if (slot === 'more') continue;

      listItem.querySelector('[ref="menuitem"]')?.removeAttribute('aria-expanded');
    }
  }

  #syncOverflowPanel = () => {
    this.#syncOverflowDisclosures();
    this.#syncOverflowTabOrder();
  };

  /**
   * Overflow items use display:contents, so each item's submenu sits in DOM order
   * before the next top-level link. Move submenus to the end of the overflow panel
   * so keyboard users reach all parent links (e.g. About) before submenu content.
   */
  #syncOverflowTabOrder = () => {
    const overflowList = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]');
    if (!(overflowList instanceof HTMLElement)) return;

    for (const listItem of this.querySelectorAll('.menu-list__list-item:not([slot="overflow"])')) {
      const ownerId = listItem.dataset.overflowTabOrderOwner;
      if (!ownerId) continue;

      const relocated = overflowList.querySelector(`[ref="submenu[]"][data-overflow-owner="${ownerId}"]`);
      if (relocated instanceof HTMLElement && relocated.parentElement !== listItem) {
        listItem.appendChild(relocated);
      }

      delete listItem.dataset.overflowTabOrderOwner;
      delete listItem.dataset.overflowTabOrderId;
      relocated?.removeAttribute('data-overflow-owner');
    }

    const overflowItems = [...this.querySelectorAll('.menu-list__list-item[slot="overflow"]')];

    overflowItems.forEach((listItem, index) => {
      if (!(listItem instanceof HTMLElement)) return;

      let submenu = listItem.querySelector(':scope > [ref="submenu[]"]');
      if (!(submenu instanceof HTMLElement)) {
        const ownerId = listItem.dataset.overflowTabOrderOwner;
        if (ownerId) {
          submenu = overflowList.querySelector(`[ref="submenu[]"][data-overflow-owner="${ownerId}"]`);
        }
      }

      if (!(submenu instanceof HTMLElement)) return;

      if (!listItem.dataset.overflowTabOrderOwner) {
        const menuItem = listItem.querySelector('[ref="menuitem"]');
        const href = menuItem?.getAttribute('href');
        listItem.dataset.overflowTabOrderOwner = href || `overflow-${index}`;
      }

      submenu.dataset.overflowOwner = listItem.dataset.overflowTabOrderOwner;

      if (submenu.parentElement !== overflowList) {
        overflowList.appendChild(submenu);
      }
    });

    for (const listItem of overflowItems) {
      const ownerId = listItem instanceof HTMLElement ? listItem.dataset.overflowTabOrderOwner : '';
      if (!ownerId) continue;

      const submenu = overflowList.querySelector(`[ref="submenu[]"][data-overflow-owner="${ownerId}"]`);
      if (submenu instanceof HTMLElement) {
        overflowList.appendChild(submenu);
      }
    }
  };

  #syncOverflowDisclosures = () => {
    for (const listItem of this.querySelectorAll('.menu-list__list-item')) {
      const disclosure = listItem.querySelector('[ref="disclosure[]"]');
      if (!(disclosure instanceof HTMLElement)) continue;

      disclosure.removeAttribute('hidden');
      disclosure.removeAttribute('aria-hidden');
      disclosure.removeAttribute('tabindex');

      const submenuInner = listItem.querySelector('.menu-list__submenu-inner');
      if (submenuInner?.id) {
        disclosure.setAttribute('aria-controls', submenuInner.id);
      } else {
        disclosure.removeAttribute('aria-controls');
      }

      disclosure.setAttribute('aria-haspopup', 'true');
      if (!disclosure.hasAttribute('aria-expanded')) {
        disclosure.setAttribute('aria-expanded', 'false');
      }
    }
  };

  /** @param {HTMLElement | null} menuItem */
  #clearOverflowSubmenuActive() {
    for (const listItem of this.querySelectorAll('.menu-list__list-item[slot="overflow"]')) {
      const menuItem = findMenuItem(listItem);
      const submenu = menuItem ? findSubmenu(menuItem) : null;
      if (!(submenu instanceof HTMLElement)) continue;

      delete submenu.dataset.active;
      submenu.inert = true;
    }
  }

  /**
   * @param {HTMLElement | null} menuItem
   * @param {{ interactive?: boolean }} [options]
   */
  #setOverflowSubmenuActive(menuItem, { interactive = true } = {}) {
    this.#clearOverflowSubmenuActive();
    if (!(menuItem instanceof HTMLElement)) return;

    const submenu = findSubmenu(menuItem);
    if (!submenu) return;

    submenu.dataset.active = '';
    submenu.inert = !interactive;
  }

  /**
   * @param {boolean} expanded
   */
  #setMenuExpanded(expanded) {
    if (expanded) {
      this.ariaExpanded = 'true';
    } else {
      this.removeAttribute('aria-expanded');
    }
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
    activeOverflowItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || event.movementY >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    window.clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  /**
   * Whether the open menu path is the More trigger / overflow panel (not a top-level mega item).
   * @returns {boolean}
   */
  #isOverflowMenuPathActive() {
    return this.dataset.overflowExpanded === 'true';
  }

  /**
   * Hit-test the last pointer position for the More trigger or overflow panel.
   * @returns {boolean}
   */
  #isPointerOverOverflowPath() {
    const target = document.elementFromPoint(this.#lastPointer.x, this.#lastPointer.y);
    if (!(target instanceof Node)) return false;

    return this.#isMovingToOverflowMenu(target);
  }

  /**
   * Whether the More trigger, overflow panel, or slotted overflow items are still hovered.
   * @returns {boolean}
   */
  #shouldKeepOverflowOpen() {
    const moreItem = this.querySelector('.menu-list__list-item[slot="more"]');
    if (moreItem?.matches(':hover')) return true;

    const overflowHost = this.refs.overflowMenu;
    if (overflowHost) {
      for (const item of overflowHost.querySelectorAll('[slot="overflow"]')) {
        if (item.matches(':hover')) return true;
      }
    }

    return this.#isOverflowMenuHovered();
  }

  /**
   * Find the first overflowing menu item shown by the More trigger.
   * @returns {HTMLElement | null}
   */
  #getFirstOverflowMenuItem() {
    const menuItem = this.refs.overflowMenu?.querySelector('[slot="overflow"] [ref="menuitem"]');
    return menuItem instanceof HTMLElement ? menuItem : null;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent | { target: Element }} event
   * @param {{ forceOpen?: boolean }} [options]
   */
  activate = (event, options = {}) => {
    if (!(event.target instanceof Element) || !this.headerComponent) return;

    clearTimeout(this.#closeDelayTimer);
    this.#closeDelayTimer = undefined;

    const item = findMenuItem(event.target);
    if (!item) return;

    const listItem = item.closest('.menu-list__list-item');
    const slot = listItem instanceof HTMLElement ? listItem.slot : '';
    const isMoreTrigger = slot === 'more';
    const forceOpen = options.forceOpen === true;

    // Tab to MORE only highlights the label — open the overflow panel from its disclosure.
    if (isMoreTrigger && !forceOpen && this.#isKeyboardTabFocus(event, listItem)) {
      logHeaderMenu('activate.skip-more-keyboard-tab', {
        label: this.#menuItemLabel(item),
        expected: 'Tab to MORE chevron, then Enter/Space to open overflow panel',
      });
      if (this.#state.activeItem && this.#state.activeItem !== item) {
        this.#deactivate(this.#state.activeItem, { force: true });
      }
      return;
    }

    if (!forceOpen && item == this.#state.activeItem) return;

    const isDefaultSlot = slot === '';
    const itemHasSubmenu = Boolean(findSubmenu(item));
    const overflowItem = isMoreTrigger ? this.#getFirstOverflowMenuItem() : null;

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;
    const previouslyActiveOverflowItem = this.#state.activeOverflowItem;

    if (previouslyActiveItem) {
      this.#setExpandedState(previouslyActiveItem, false);
      const previousSubmenu = findSubmenu(previouslyActiveItem);
      if (previousSubmenu) {
        delete previousSubmenu.dataset.active;
        previousSubmenu.inert = true;
      }
    }
    if (previouslyActiveOverflowItem && previouslyActiveOverflowItem !== previouslyActiveItem) {
      this.#setExpandedState(previouslyActiveOverflowItem, false);
      const previousOverflowSubmenu = findSubmenu(previouslyActiveOverflowItem);
      if (previousOverflowSubmenu) {
        delete previousOverflowSubmenu.dataset.active;
        previousOverflowSubmenu.inert = true;
      }
    }

    this.#state.activeItem = item;
    this.#state.activeOverflowItem = overflowItem;
    this.#setMenuExpanded(itemHasSubmenu || isMoreTrigger || Boolean(overflowItem));

    const isKeyboardTabFocus = this.#isKeyboardTabFocus(event, listItem);

    if (isMoreTrigger) {
      /* Pointer/hover opens the first overflow submenu; keyboard users open from disclosure. */
      this.#setOverflowSubmenuActive(overflowItem, {
        interactive: forceOpen ? false : !isKeyboardTabFocus,
      });
    } else if (slot === 'overflow') {
      this.#setOverflowSubmenuActive(item, { interactive: forceOpen || !isKeyboardTabFocus });
    } else {
      this.#clearOverflowSubmenuActive();
    }

    const overflowItemSubmenu = isMoreTrigger ? findSubmenu(overflowItem) : null;
    let submenu = findSubmenu(item) || (overflowItemSubmenu ? this.overflowMenu : null);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      clearTimeout(this.#hoverDispatchTimer);
      this.#hoverDispatchTimer = undefined;
      const committedItem = item;
      if (event instanceof FocusEvent) {
        this.dispatchEvent(new MegaMenuHoverEvent());
      } else {
        this.#hoverDispatchTimer = setTimeout(() => {
          this.#hoverDispatchTimer = undefined;
          if (this.#state.activeItem === committedItem) {
            this.dispatchEvent(new MegaMenuHoverEvent());
          }
        }, HOVER_COMMIT_DELAY_MS);
      }

      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';

      // Expose the open submenu to focus and assistive tech. Collapsed submenus stay
      // inert so their contents cannot be reached (visibility:hidden alone can be
      // overridden by descendants that re-assert visibility).
      // Keyboard tab focus shows the panel but keeps submenu links out of tab order
      // until Enter/Space on the disclosure button (second tab stop).
      const deferSubmenuTabbing =
        !forceOpen &&
        isKeyboardTabFocus &&
        (itemHasSubmenu || slot === 'overflow') &&
        !isMoreTrigger;

      submenu.inert = deferSubmenuTabbing;

      if (isMoreTrigger) {
        this.#setExpandedState(item, forceOpen || !isKeyboardTabFocus);
      } else if (itemHasSubmenu || slot === 'overflow') {
        this.#setExpandedState(item, !deferSubmenuTabbing);
      } else {
        this.#setExpandedState(item, false);
      }

      // Cleanup any existing mutation observer from previous menu activations
      this.#cleanupMutationObserver();

      // Monitor DOM mutations to catch deferred content injection (from section hydration)
      this.#submenuMutationObserver = new MutationObserver(() => {
        requestAnimationFrame(() => {
          // Double requestAnimationFrame to ensure the height is properly calculated and not defaulting to the contain-intrinsic-size
          requestAnimationFrame(() => {
            if (submenu.offsetHeight > 0) {
              this.headerComponent?.style.setProperty('--submenu-height', `${submenu.offsetHeight}px`);
              this.#cleanupMutationObserver();
            }
          });
        });
      });
      this.#submenuMutationObserver.observe(submenu, { childList: true, subtree: true });

      // Auto-disconnect after 500ms to prevent memory leaks
      setTimeout(() => {
        this.#cleanupMutationObserver();
      }, 500);
    }

    let finalHeight = submenu?.offsetHeight || 0;

    // For overflow menu, the height needs to be either content of the submenu or the total height of the menu list links
    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      // If there is no content to open, don't try to open it
      finalHeight = 0;
    }

    const headerVisibleHeight = this.#getHeaderVisibleHeight();

    this.headerComponent.style.setProperty('--submenu-height', `${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight, headerVisibleHeight);
    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);

    const submenuState = this.#submenuSnapshot(item);
    logHeaderMenu('activate', {
      label: this.#menuItemLabel(item),
      slot: slot || 'default',
      forceOpen,
      keyboardTabFocus: isKeyboardTabFocus,
      overflowExpanded: this.dataset.overflowExpanded,
      submenuHeight: finalHeight,
      submenu: submenuState,
      expectedNext:
        submenuState.hasSubmenu && submenuState.inert
          ? 'Tab to disclosure chevron, then Enter/Space to enter submenu'
          : slot === 'more'
            ? 'Tab into overflow links'
            : submenuState.hasSubmenu
              ? 'Submenu content is interactive'
              : 'No submenu for this item',
    });
  };

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  /**
   * @param {FocusEvent | PointerEvent} event
   * @param {Element | null} listItem
   * @param {HTMLElement | null} pairedSubmenu
   * @returns {boolean}
   */
  #isFocusMovingWithinMenuItem(event, listItem, pairedSubmenu) {
    if (!(event.relatedTarget instanceof Node) || !(event.target instanceof Element)) return false;
    if (!(listItem instanceof HTMLElement)) return false;

    const { target, relatedTarget } = event;

    if (listItem.contains(target) && listItem.contains(relatedTarget)) return true;

    if (pairedSubmenu instanceof HTMLElement) {
      if (listItem.contains(target) && pairedSubmenu.contains(relatedTarget)) return true;
      if (pairedSubmenu.contains(target) && listItem.contains(relatedTarget)) return true;
    }

    return target.contains(relatedTarget);
  }

  /**
   * Whether the pointer is moving into the overflow panel or its slotted items.
   * Slotted overflow links live in the light DOM; the panel container lives in
   * shadow DOM, so `closest('[slot="overflow"]')` alone misses panel padding.
   * @param {EventTarget | null} relatedTarget
   * @returns {boolean}
   */
  #isMovingToOverflowMenu(relatedTarget) {
    if (!(relatedTarget instanceof Node)) return false;

    if (relatedTarget instanceof Element && relatedTarget.closest('[slot="overflow"], [slot="more"]')) {
      return true;
    }

    const overflowHost = this.refs.overflowMenu;
    if (!overflowHost) return false;

    return (
      relatedTarget === overflowHost ||
      overflowHost.contains(relatedTarget) ||
      (relatedTarget instanceof Element && relatedTarget.closest('overflow-list') === overflowHost)
    );
  }

  /**
   * Whether the open overflow panel or its contents are still hovered.
   * @returns {boolean}
   */
  #isOverflowMenuHovered() {
    const overflowSubmenu = this.#state.activeOverflowItem
      ? findSubmenu(this.#state.activeOverflowItem)
      : null;

    return Boolean(
      this.overflowListHovered ||
        this.overflowMenu?.matches(':hover') ||
        overflowSubmenu?.matches(':hover') ||
        this.refs.overflowMenu?.matches(':hover')
    );
  }

  /**
   * Delay before closing when leaving the More trigger or overflow panel.
   * @returns {number}
   */
  #getCloseDelayMs() {
    return this.#isOverflowMenuPathActive() ? OVERFLOW_CLOSE_DELAY_MS : 100;
  }

  deactivate(event) {
    if (!(event.target instanceof Element)) return;

    const menu =
      findSubmenu(this.#state.activeItem) || findSubmenu(this.#state.activeOverflowItem);
    const listItem = event.target instanceof Element ? event.target.closest('.menu-list__list-item') : null;
    const pairedSubmenu = listItem ? findSubmenu(findMenuItem(listItem) ?? listItem) : null;
    // Keep the submenu open while focus moves between the link, its disclosure
    // button, and the submenu contents. Overflow submenus are relocated outside
    // the list item, so check both the trigger row and the paired submenu.
    const isMovingWithinItem = this.#isFocusMovingWithinMenuItem(event, listItem, pairedSubmenu);
    const isMovingWithinMenu = event.relatedTarget instanceof Node && menu?.contains(document.activeElement);
    const isMovingToSubmenu =
      event.relatedTarget instanceof Node &&
      (event.type === 'blur' || event.type === 'pointerleave') &&
      menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu = this.#isMovingToOverflowMenu(event.relatedTarget);

    if (isMovingWithinItem || isMovingWithinMenu || isMovingToOverflowMenu || isMovingToSubmenu) {
      logHeaderMenu('deactivate.retain', {
        reason: isMovingWithinItem
          ? 'focus-within-item'
          : isMovingWithinMenu
            ? 'focus-within-menu'
            : isMovingToOverflowMenu
              ? 'moving-to-overflow'
              : 'moving-to-submenu',
        from: this.#elementLabel(event.target instanceof Element ? event.target : null),
        to:
          event.relatedTarget instanceof Element ? this.#elementLabel(event.relatedTarget) : null,
      });
      if (this.#state.activeItem && !isMovingToOverflowMenu && !this.#isOverflowMenuPathActive()) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    if (menu?.matches(':hover') || this.#shouldKeepOverflowOpen()) return;

    if (event.type === 'pointerleave') {
      clearTimeout(this.#closeDelayTimer);
      this.#closeDelayTimer = setTimeout(() => {
        this.#closeDelayTimer = undefined;
        const activeItem = this.#state.activeItem;
        if (!activeItem) return;

        const activeSubmenu =
          findSubmenu(activeItem) || findSubmenu(this.#state.activeOverflowItem);
        const listItem = activeItem.closest('.menu-list__list-item');
        if (
          activeSubmenu?.matches(':hover') ||
          listItem?.matches(':hover') ||
          this.#shouldKeepOverflowOpen() ||
          this.#isPointerOverOverflowPath()
        ) {
          return;
        }

        this.#deactivate();
      }, this.#getCloseDelayMs());
      return;
    }

    this.#deactivate();
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, { force = false } = {}) => {
    if (!item || item != this.#state.activeItem) return;

    const activeSubmenu = findSubmenu(item);

    // Don't deactivate if the overflow menu or overflow list is still being hovered,
    // unless the close was explicitly requested (Escape or the disclosure toggle),
    // which must close regardless of pointer position.
    if (!force && (activeSubmenu?.matches(':hover') || this.#shouldKeepOverflowOpen())) {
      logHeaderMenu('deactivate.skip', {
        label: this.#menuItemLabel(item),
        reason: activeSubmenu?.matches(':hover') ? 'submenu-hovered' : 'overflow-path-hovered',
      });
      return;
    }

    logHeaderMenu('deactivate', {
      label: this.#menuItemLabel(item),
      force,
    });

    clearTimeout(this.#closeDelayTimer);
    this.#closeDelayTimer = undefined;
    clearTimeout(this.#hoverDispatchTimer);
    this.#hoverDispatchTimer = undefined;

    this.headerComponent?.style.setProperty('--submenu-height', '0px');
    this.#setFullOpenHeaderHeight(0, 0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(item) || (item.closest('[slot="more"]') ? this.overflowMenu : null);
    const activeOverflowItem = this.#state.activeOverflowItem;

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeOverflowItem = null;
    this.#setMenuExpanded(false);
    this.#setExpandedState(item, false);
    this.#sanitizeLeafLinkAria();
    if (activeOverflowItem && activeOverflowItem !== item) {
      this.#setExpandedState(activeOverflowItem, false);
    }

    this.#clearOverflowSubmenuActive();

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
      submenu.inert = true;
    }

  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const overflowList = this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]');
    const relocatedSubmenus = overflowList?.querySelectorAll(':scope > [ref="submenu[]"]') ?? [];

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
      relocatedSubmenus.forEach((submenu) => {
        if (submenu instanceof HTMLElement) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Read the visible header height before submenu height writes invalidate layout.
   * @returns {number}
   */
  #getHeaderVisibleHeight() {
    if (!this.headerComponent) return 0;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    return isOverlapSituation && this.headerComponent.offsetHeight > 0
      ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
      : this.headerComponent.offsetHeight;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   * @param {number} headerVisibleHeight
   */
  #setFullOpenHeaderHeight(submenuHeight, headerVisibleHeight) {
    if (!this.headerComponent) return;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + headerVisibleHeight;

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };

  #cleanupMutationObserver() {
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver = null;
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  // `element` may be the list item itself (hover/focus routed via the `<li>`) or a
  // descendant such as the disclosure button (click routed via the button).
  const listItem = element.closest('.menu-list__list-item') ?? element;
  return listItem.querySelector('[ref="menuitem"]');
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const listItem = element?.closest?.('.menu-list__list-item');
  if (!(listItem instanceof HTMLElement)) return null;

  if (listItem.slot === 'overflow') {
    const ownerId = listItem.dataset.overflowTabOrderOwner;
    if (ownerId) {
      const overflowHost = listItem.closest('overflow-list');
      const overflowList = overflowHost?.shadowRoot?.querySelector('[part="overflow-list"]');
      const relocated = overflowList?.querySelector(
        `[ref="submenu[]"][data-overflow-owner="${ownerId}"]`
      );
      if (relocated instanceof HTMLElement) return relocated;
    }
  }

  const submenu = listItem.querySelector(':scope > [ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

